import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ListRow } from "./apiTypes";

function segLabel(s: string) {
  if (s === "na") return "N/A";
  return s;
}

export default function AdminResponses() {
  const [searchParams] = useSearchParams();
  const surveyId = searchParams.get("survey_id") ?? "";
  const [rows, setRows] = useState<ListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortScore, setSortScore] = useState(false);
  const pageSize = 25;

  const [segment, setSegment] = useState("");
  const [profileF, setProfileF] = useState("");
  const [signalF, setSignalF] = useState("");
  const [commitmentF, setCommitmentF] = useState("");
  const [emailF, setEmailF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minScore, setMinScore] = useState("");
  const [maxScore, setMaxScore] = useState("");

  useEffect(() => {
    const sig = searchParams.get("signal") ?? "";
    const prof = searchParams.get("profile") ?? "";
    const em = searchParams.get("has_email") ?? "";
    setSignalF(sig);
    setProfileF(prof);
    setEmailF(em === "1" || em === "0" ? em : "");
    setPage(1);
  }, [searchParams]);

  const filterParams = useMemo(() => {
    const q = new URLSearchParams();
    if (segment) q.set("segment", segment);
    if (profileF) q.set("profile", profileF);
    if (signalF) q.set("signal", signalF);
    if (commitmentF) q.set("commitment", commitmentF);
    if (emailF) q.set("has_email", emailF);
    if (dateFrom) q.set("date_from", dateFrom);
    if (dateTo) q.set("date_to", dateTo);
    if (minScore) q.set("score_min", minScore);
    if (maxScore) q.set("score_max", maxScore);
    if (sortScore) q.set("sort", "score");
    if (surveyId) q.set("survey_id", surveyId);
    return q;
  }, [
    segment,
    profileF,
    signalF,
    commitmentF,
    emailF,
    dateFrom,
    dateTo,
    minScore,
    maxScore,
    sortScore,
    surveyId,
  ]);

  const listQuery = useMemo(() => {
    const q = new URLSearchParams(filterParams);
    q.set("page", String(page));
    q.set("page_size", String(pageSize));
    return q.toString();
  }, [filterParams, page]);

  const exportHref = `/api/admin/export.csv?${filterParams.toString()}`;

  const loadRows = useCallback(async () => {
    const res = await fetch(`/api/admin/responses?${listQuery}`, {
      credentials: "include",
    });
    if (!res.ok) return;
    const data = (await res.json()) as {
      items: ListRow[];
      total: number;
    };
    setRows(data.items);
    setTotal(data.total);
  }, [listQuery]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={segment}
          onChange={(e) => {
            setSegment(e.target.value);
            setPage(1);
          }}
          className="border rounded p-2"
        >
          <option value="">Segmento</option>
          <option value="bajo">bajo</option>
          <option value="medio">medio</option>
          <option value="alto">alto</option>
          <option value="na">N/A</option>
        </select>
        <select
          value={profileF}
          onChange={(e) => {
            setProfileF(e.target.value);
            setPage(1);
          }}
          className="border rounded p-2"
        >
          <option value="">Perfil</option>
          <option value="p1">p1</option>
          <option value="p2">p2</option>
          <option value="p2n">p2n</option>
          <option value="p3">p3</option>
        </select>
        <select
          value={signalF}
          onChange={(e) => {
            setSignalF(e.target.value);
            setPage(1);
          }}
          className="border rounded p-2"
        >
          <option value="">Señal</option>
          <option value="hot">hot</option>
          <option value="warm">warm</option>
          <option value="cold">cold</option>
        </select>
        <select
          value={commitmentF}
          onChange={(e) => {
            setCommitmentF(e.target.value);
            setPage(1);
          }}
          className="border rounded p-2"
        >
          <option value="">Compromiso</option>
          <option value="yes">sí</option>
          <option value="maybe">tal vez</option>
          <option value="no">no</option>
        </select>
        <select
          value={emailF}
          onChange={(e) => {
            setEmailF(e.target.value);
            setPage(1);
          }}
          className="border rounded p-2"
        >
          <option value="">Email</option>
          <option value="1">dejó</option>
          <option value="0">no</option>
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
          className="border rounded p-2"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
          className="border rounded p-2"
        />
        <input
          type="number"
          placeholder="score min"
          value={minScore}
          onChange={(e) => {
            setMinScore(e.target.value);
            setPage(1);
          }}
          className="w-24 border rounded p-2"
        />
        <input
          type="number"
          placeholder="score max"
          value={maxScore}
          onChange={(e) => {
            setMaxScore(e.target.value);
            setPage(1);
          }}
          className="w-24 border rounded p-2"
        />
        <button type="button" className="border rounded px-3 py-2" onClick={() => void loadRows()}>
          Aplicar
        </button>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={sortScore}
            onChange={(e) => {
              setSortScore(e.target.checked);
              setPage(1);
            }}
          />
          Orden por score
        </label>
        <a className="underline ml-auto" href={exportHref} target="_blank" rel="noreferrer">
          Export CSV (filtrado)
        </a>
      </div>

      <p className="text-pp-muted">
        {total} resultados · página {page} de {totalPages}
      </p>

      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-pp-soft">
            <tr>
              <th className="p-2">Fecha</th>
              <th className="p-2">Perfil</th>
              <th className="p-2">Segmento</th>
              <th className="p-2">Score</th>
              <th className="p-2">Señal</th>
              <th className="p-2">Compromiso</th>
              <th className="p-2">Email</th>
              <th className="p-2">Fricción q1</th>
              <th className="p-2">Tiempo q2</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 whitespace-nowrap">{r.created_at}</td>
                <td className="p-2">{r.profile}</td>
                <td className="p-2">{segLabel(r.segment)}</td>
                <td className="p-2 tabular-nums">{r.score}</td>
                <td className="p-2">{r.signal_level}</td>
                <td className="p-2">{r.commitment_level ?? "—"}</td>
                <td className="p-2">{r.has_email === 1 ? "sí" : "—"}</td>
                <td className="p-2 max-w-[120px] truncate" title={r.q1_friction_freq ?? ""}>
                  {r.q1_friction_freq ?? "—"}
                </td>
                <td className="p-2 max-w-[120px] truncate" title={r.q2_time_week ?? ""}>
                  {r.q2_time_week ?? "—"}
                </td>
                <td className="p-2">
                  <Link to={`/admin/responses/${r.id}`} className="underline">
                    Ver
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          className="border rounded px-3 py-1.5 disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Anterior
        </button>
        <button
          type="button"
          className="border rounded px-3 py-1.5 disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
}
